/**
 * ResponseDetailInline Component
 *
 * Full-width inline response detail view that renders in normal page flow (not as modal).
 * Displays response content with site header/nav/footer always visible.
 * Shows ONLY the detail view (no sidebar or response list).
 *
 * A11y Foundation: Semantic HTML, button elements, logical DOM order.
 */

import React, { useEffect } from 'react';
import { logEvent, createTimer, getCorrelationId } from '../../../utils/telemetry';
import { BackControlsDual } from '../shared/BackControlsDual';
import { useDevice } from '../../../features/consensus/hooks/useDevice';

export interface ResponseDetailInlineProps {
  onClose: () => void;
  onNavigateToParent?: () => void;
  onNavigateToAll?: () => void;
  parentTitle?: string;
  children: React.ReactNode;
  headerActions?: React.ReactNode;
  navigationContent?: React.ReactNode;
}

export const ResponseDetailInline: React.FC<ResponseDetailInlineProps> = ({
  onClose,
  onNavigateToParent,
  onNavigateToAll,
  parentTitle,
  children,
  headerActions,
  navigationContent,
}) => {
  const { isMobile } = useDevice();

  // Log open event when component mounts
  useEffect(() => {
    const timer = createTimer();
    const correlationId = getCorrelationId();

    logEvent({
      op: 'open_details',
      component: 'ResponseDetailInline',
      correlationId,
      duration_ms: timer.end(),
      success: true,
    });
  }, []);

  const headerContainerClasses = headerActions
    ? 'px-4 py-3 flex items-center justify-between gap-3 flex-nowrap relative'
    : 'px-4 py-3 flex items-center gap-3 relative';

  return (
    <div
      className="flex flex-col w-full min-h-screen bg-slate-900"
      role="region"
      aria-label="Response Details"
    >
      {/* Sticky Header with Dual Back Controls and Actions - Hidden on mobile */}
      {!isMobile && (
        <header
          className="sticky top-0 z-[9998] bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50"
        >
          <div className={headerContainerClasses}>
            <div className="flex items-center gap-3">
              <BackControlsDual
                parentTitle={parentTitle}
                onNavigateToParent={onNavigateToParent}
                onNavigateToAll={onNavigateToAll}
                showParentButton={true}
                useAllButton={true}
              />
            </div>
            {navigationContent && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                {navigationContent}
              </div>
            )}
            {headerActions && (
              <div className="flex items-center gap-2 ml-auto flex-nowrap overflow-x-auto">
                {headerActions}
              </div>
            )}
          </div>
        </header>
      )}

      {/* Content Area */}
      <main className="flex-1 w-full">
        {children}
      </main>
    </div>
  );
};
